// SPDX-FileCopyrightText: 2026 Ebiose AI
// SPDX-License-Identifier: LicenseRef-Ebiose-Proprietary
// All rights reserved. Confidential — unauthorized distribution prohibited.

/**
 * M4 — bottom card row of the recomposed agent detail page (slice 06).
 *
 * A single card fed entirely from the already-fetched detail payload (no
 * new fetch), per the design mockup `Détail Agent.dc.html` §bottom cards:
 *
 *   - **Lineage** — origin cycle and parent agents rendered as real
 *     route links (ADR-0006, canonical deep-linkable routes), the
 *     agent's own fitness, and the design's `—` treatment for absent
 *     fields (PRD US §16). The descendants section lands in a later
 *     slice (`descendants-lineage`); this card stays extensible.
 *
 * There is no execution-costs card: `max_execution_cost` is a design
 * constraint fed to the architect, not a measurement, and no per-agent cost
 * exists to make such a card truthful (real spend settles per cycle,
 * ADR-0062). The bottom row is Lineage alone.
 *
 * The input/output JSON schemas + state vars (PRD US §15) are NOT a
 * bottom card: the design surfaces them in the Node Inspector when the
 * graph's `input` / `output` endpoint nodes are selected (the schemas
 * live at the agent-model level). See `components/NodeInspector.tsx`.
 *
 * Visual values (card border/radius, label opacity, link colour) come
 * from the design handoff token table — no invented values.
 */
import type { ReactNode } from "react"
import { Link } from "react-router-dom"
import { useAgent, useAgents, useDescendants } from "@/queries/queries.agents"

/**
 * Shared bottom-card shell — Lagon card chrome plus a mono uppercase
 * section eyebrow (design mockup `Détail Agent.dc.html` §bottom cards).
 * The `title` doubles as the card's accessible name so each card is
 * queryable as a `region` in tests.
 *
 * `skin` selects the box surface variant: hairline `.box` by default,
 * `.box-tint` for the lineage/meta family.
 */
function Card({
  title,
  children,
  skin = "box",
}: {
  title: string
  children: ReactNode
  skin?: "box" | "box-tint"
}) {
  return (
    <section
      aria-label={title}
      className={`flex min-w-0 flex-col gap-3.5 p-[18px] ${skin}`}
    >
      <h2 className="font-mono text-[11px] uppercase tracking-[0.1em] text-text-tertiary">
        {title}
      </h2>
      {children}
    </section>
  )
}

/**
 * A single lineage row: a labelled, canonical route link to the
 * referenced detail page, or the design's `—` empty treatment when the
 * id is absent. The link target is a real `to=` route (ADR-0006,
 * deep-linkable) — never onClick-only navigation. Absent ids render no
 * link, so there are no dead links.
 */
function LineageLink({
  label,
  id,
  to,
}: {
  label: string
  id: string | null
  to: (id: string) => string
}) {
  return (
    <>
      <dt className="text-text-tertiary">{label}</dt>
      <dd className="break-all font-mono">
        {id === null ? (
          "—"
        ) : (
          <Link
            to={to(id)}
            className="text-lineage underline underline-offset-[3px] transition-opacity hover:opacity-80"
          >
            {id}
          </Link>
        )}
      </dd>
    </>
  )
}

/** Shared link styling for lineage rows (Lagon lineage violet). */
const lineageLinkClass =
  "text-lineage underline underline-offset-[3px] transition-opacity hover:opacity-80"

/** Format an ISO timestamp as `dd/MM/yyyy` (date-only lineage context). */
function formatDay(iso: string): string {
  const d = new Date(iso)
  const pad = (n: number) => String(n).padStart(2, "0")
  return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()}`
}

/**
 * Plain-language nature of a meta operator, derived from its card name
 * (DEV-325 §1). The three operator families ship with stable names in
 * the local ecosystem (`graph-architect`, `graph-mutator`,
 * `graph-crossover` — cf. ebiose-core reproduction/basic-evolver
 * engines); an unrecognized name renders without a nature mention.
 */
function operatorNature(name: string): string | null {
  const n = name.toLowerCase()
  if (n.includes("architect")) return "designed by the architect"
  if (n.includes("mutat")) return "created by mutation"
  if (n.includes("cross")) return "created by crossover"
  return null
}

/**
 * Origin-cycle row (DEV-325 §3). When the detail payload carries the
 * `origin_cycle` summary, the row narrates the birth context — forge
 * name as the link text, period + outcome as muted context — with the
 * raw cycle id kept as a secondary line inside the same link. Payloads
 * without the summary (legacy cache, degraded lookup) keep the plain id
 * link; an absent id keeps the `—` treatment.
 */
function OriginCycleRow({ agent }: { agent: AgentDetails }) {
  const id = agent.origin_evolution_cycle_id
  const cycle = agent.origin_cycle ?? null
  return (
    <>
      <dt className="text-text-tertiary">origin cycle</dt>
      <dd className="break-all">
        {id === null ? (
          "—"
        ) : (
          <>
            <Link to={`/evolution-cycles/${id}`} className={lineageLinkClass}>
              {cycle?.forge_name ?? id}
              {cycle?.forge_name ? (
                <span className="block font-mono text-xs text-text-tertiary">
                  {id}
                </span>
              ) : null}
            </Link>
            {cycle ? (
              <span className="block text-xs text-text-tertiary">
                {cycle.ended_at
                  ? `ended ${formatDay(cycle.ended_at)}${
                      cycle.outcome ? ` · ${cycle.outcome}` : ""
                    }`
                  : `started ${formatDay(cycle.started_at)} · running`}
              </span>
            ) : null}
          </>
        )}
      </dd>
    </>
  )
}

/**
 * Parent-meta-agent row (DEV-325 §1). Resolves the operator that
 * produced this agent through the existing detail endpoint and leads
 * with what the operator *did* — "created by mutation (graph-mutator)"
 * — instead of an opaque id. The id stays visible as a secondary line
 * inside the link. While the lookup is in flight, has failed, or the
 * operator has no card (legacy payloads), the row falls back to the
 * plain id link.
 *
 * US §23 (issue `dashboard-audit-fixes`) — when the parent has been
 * purged, `useAgent` settles to `data === null` (graceful 404, no
 * retry storm). That state gets its own affordance instead of a dead
 * link: a muted "parent unavailable" label, id kept for reference.
 */
function OperatorRow({ agent }: { agent: AgentDetails }) {
  const id = agent.parent_meta_agent_id
  const { data, isSuccess } = useAgent(id ?? undefined)
  const purged = isSuccess && data === null

  const model = data?.agent_model as { card?: { name?: string } } | undefined
  const cardName =
    typeof model?.card?.name === "string" && model.card.name !== ""
      ? model.card.name
      : null
  const nature = cardName ? operatorNature(cardName) : null

  return (
    <>
      <dt className="text-text-tertiary">parent meta agent</dt>
      <dd className="break-all">
        {id === null ? (
          "—"
        ) : purged ? (
          <span className="inline-flex flex-col text-text-tertiary">
            <span>parent unavailable</span>
            <span className="font-mono text-xs">{id}</span>
          </span>
        ) : (
          <>
            {nature ? (
              <span className="block text-xs text-text-secondary">{nature}</span>
            ) : null}
            <Link to={`/agents/${id}`} className={lineageLinkClass}>
              {cardName ?? id}
              {cardName ? (
                <span className="block font-mono text-xs text-text-tertiary">
                  {id}
                </span>
              ) : null}
            </Link>
          </>
        )}
      </dd>
    </>
  )
}

/**
 * Cohort-rank row (DEV-325 §2). Ranks the agent by fitness among the
 * agents born in the same cycle (existing `origin_evolution_cycle_id`
 * list filter — no new endpoint) and links to that cycle's dashboard,
 * where the cohort is the leaderboard. Standard competition ranking:
 * rank = 1 + number of cohort agents with strictly higher fitness.
 * Unscored agents (null fitness) and agents without an origin cycle
 * keep the `—` treatment.
 */
function CohortRankRow({ agent }: { agent: AgentDetails }) {
  const cycleId = agent.origin_evolution_cycle_id
  const fitness = agent.fitness
  const enabled = cycleId !== null && fitness !== null
  const { data } = useAgents(
    { origin_evolution_cycle_id: cycleId ?? undefined },
    { enabled },
  )

  let content: ReactNode = "—"
  // Render the rank only when the agent is itself part of the returned
  // cohort — a sane cohort always contains it; anything else (stale
  // filter, scope mismatch) would yield a misleading "#1 / 0".
  if (
    cycleId !== null &&
    fitness !== null &&
    data &&
    data.items.some((peer) => peer.agent_id === agent.agent_id)
  ) {
    const cohort = data.items
    const rank =
      1 +
      cohort.filter((peer) => peer.fitness !== null && peer.fitness > fitness)
        .length
    content = (
      <Link
        to={`/evolution-cycles/${encodeURIComponent(cycleId)}`}
        className={lineageLinkClass}
      >
        #{rank} / {cohort.length} agents of its cycle
      </Link>
    )
  }

  return (
    <>
      <dt className="text-text-tertiary">cohort rank</dt>
      <dd>{content}</dd>
    </>
  )
}

/**
 * Survival row (DEV-325 §4) — in how many cycles after its birth the
 * agent was reselected as a candidate. A strong evolutionary-quality
 * signal when positive; 0 (or a payload predating the field) keeps the
 * `—` treatment per AC.
 */
function LaterCyclesRow({ agent }: { agent: AgentDetails }) {
  const count = agent.later_cycle_candidate_count ?? 0
  return (
    <>
      <dt className="text-text-tertiary">later cycles</dt>
      <dd>
        {count > 0 ? (
          <span className="text-done">
            reselected in {count} later {count === 1 ? "cycle" : "cycles"}
          </span>
        ) : (
          "—"
        )}
      </dd>
    </>
  )
}

/**
 * Descendants section inside the Lineage card (US §17). Lists the agents
 * the displayed agent produced — fetched by the appropriate parent
 * lineage field (`parent_evolver_id` for evolvers, `parent_meta_agent_id`
 * for meta agents) through the M5 `useDescendants` hook. Each descendant
 * is a canonical `/agents/:id` route link (ADR-0006) carrying its
 * fitness.
 *
 * The hook stays disabled for domain agents (null role), so a domain
 * agent's page never fetches and this section renders nothing. When an
 * evolver / meta agent has no descendants yet, an empty-state `—` shows
 * instead of a blank section.
 */
function DescendantsSection({ agent }: { agent: AgentDetails }) {
  const { data, isError } = useDescendants(agent.agent_id, agent.role)

  // Only evolver / meta agents have descendants. For any other role the
  // hook is disabled and returns no data — render nothing (no heading,
  // no section) per AC4.
  if (agent.role !== "evolver" && agent.role !== "meta") {
    return null
  }

  const descendants = data?.items ?? []

  return (
    <section aria-label="Descendants" className="space-y-1.5 pt-1">
      <h3 className="font-mono text-[10.5px] uppercase tracking-[0.06em] text-text-tertiary">
        descendants
      </h3>
      {isError ? (
        // A failed lookup must not masquerade as "no descendants" — the
        // empty treatment is reserved for a successful empty answer.
        <p role="status" className="text-sm text-expired">
          Could not load descendants.
        </p>
      ) : descendants.length === 0 ? (
        <p className="text-sm text-text-tertiary">—</p>
      ) : (
        <ul className="space-y-1 text-sm">
          {descendants.map((child) => (
            <li key={child.agent_id} className="flex flex-row gap-2">
              <Link
                to={`/agents/${child.agent_id}`}
                className="break-all font-mono text-lineage underline underline-offset-[3px] transition-opacity hover:opacity-80"
              >
                {child.agent_id}
              </Link>
              <span className="text-done">
                {child.fitness === null ? "—" : child.fitness.toFixed(3)}
              </span>
            </li>
          ))}
        </ul>
      )}
    </section>
  )
}

/**
 * Lineage card — the agent's genealogical narrative (US §16 + DEV-325):
 * origin cycle with its forge name / period / outcome, fitness rank in
 * the birth cohort, parent agents (the meta operator resolved to its
 * card name and nature), the agent's own fitness, the
 * later-cycle-reselection survival signal, and (for evolver / meta
 * agents) the descendants the agent produced (US §17, via the M5
 * hook). Absent fields get the `—` treatment.
 *
 * Exported so the agent detail page can render it standalone on
 * evolver / meta agent pages (which have no graph and therefore no
 * `BottomCards` row).
 */
export function LineageCard({ agent }: { agent: AgentDetails }) {
  return (
    <Card title="Lineage">
      <dl className="grid grid-cols-[max-content_1fr] gap-x-3.5 gap-y-1.5 text-sm">
        <OriginCycleRow agent={agent} />
        <CohortRankRow agent={agent} />
        <LineageLink
          label="parent evolver"
          id={agent.parent_evolver_id}
          to={(id) => `/agents/${id}`}
        />
        <OperatorRow agent={agent} />
        <dt className="text-text-tertiary">fitness</dt>
        <dd className="text-done">
          {agent.fitness === null ? "—" : agent.fitness.toFixed(3)}
        </dd>
        <LaterCyclesRow agent={agent} />
      </dl>
      <DescendantsSection agent={agent} />
    </Card>
  )
}

/**
 * The bottom card row — Lineage alone (mockup §bottom cards). Reads the
 * lineage envelope from the detail payload. Single column: no per-agent cost
 * measurement exists to justify a costs sibling (see the header note).
 */
export function BottomCards({ agent }: { agent: AgentDetails }) {
  return (
    <div className="grid grid-cols-1 items-stretch gap-3.5">
      <LineageCard agent={agent} />
    </div>
  )
}
